import React, { useState } from "react";
import AuthInput from "./AuthInput";
import AuthButton from "./AuthButton";
import DiscordButton from "./DiscordButton";
import GoogleButton from "./GoogleButton";
import { lookupEmail, signIn, signUp } from "../services/AuthService";
import { getUser } from "../services/UserService";
import { defaultPostAuthPath, heldPostAuthPath } from "../utils/dealOnboarding";
import { useTranslation } from "../context/LanguageContext";
import { track } from "../utils/analytics";
import { ArrowLeft } from "lucide-react";
import {
  consumePendingAuthIntent,
  persistPendingAuthReturn,
} from "../utils/authReturn";

export type AuthStep = "email" | "password";

// Shotbase-style dark controls for the minimal sign-in column.
const OAUTH_BTN_CLASS =
  "!h-11 !rounded-xl !border-0 !py-0 !bg-[#232327] hover:!bg-[#2C2C31] !text-[#FAFAFA] !text-sm !font-medium !shadow-none hover:!shadow-none !transition-colors";

const AUTH_INPUT_CLASS =
  "!h-12 !rounded-xl !border !border-[#27272A] !bg-[#18181B] hover:!bg-[#1D1D21] focus:!border-[#7C3AED] focus:!ring-4 focus:!ring-[#7C3AED]/20 !px-4 !py-0 !text-base !text-[#FAFAFA] placeholder:!text-[#71717A]";

const PRIMARY_CLASS =
  "!h-12 !rounded-xl !py-0 !text-[15px] !font-medium !bg-[#7C3AED] hover:!bg-[#6D28D9] !text-white !shadow-[0_8px_24px_rgba(124,58,237,0.35)] active:!scale-[0.98] !transition-all";

/**
 * Email-first sign in / sign up with Google and Discord, used by the /get-started page and by the
 * sign-in modal on the free pages, so both offer exactly the same options.
 */
export default function AuthForm({
  returnTo,
  authSource,
  onStepChange,
  titleAs = "h1",
}: {
  returnTo?: string;
  authSource?: string;
  onStepChange?: (step: AuthStep) => void;
  /** h1 on the page; h2 inside the modal, which sits on a page that has its own h1. */
  titleAs?: "h1" | "h2";
}) {
  const Title = titleAs;
  const { t } = useTranslation();

  const [step, setStep] = useState<AuthStep>("email");
  const [exists, setExists] = useState<boolean | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Signup marketing-email consent box, pre-checked by product decision.
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    persistPendingAuthReturn(returnTo, authSource);
  }, [authSource, returnTo]);

  React.useEffect(() => {
    onStepChange?.(step);
  }, [onStepChange, step]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError(t("errors.emailRequired"));
      return;
    }
    setIsLoading(true);
    try {
      const result = await lookupEmail(trimmed);
      setEmail(trimmed);
      setExists(result.exists);
      setStep("password");
      track("auth_email_continue", { exists: result.exists });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unable to verify email");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!password) {
      setError(t("errors.passwordRequired"));
      return;
    }
    if (!exists && password.length < 8) {
      setError(t("auth.minCharacters"));
      return;
    }

    setIsLoading(true);
    try {
      if (exists) {
        await signIn(email, password);
        const signedIn = await getUser();
        const held = heldPostAuthPath(signedIn);
        if (held) {
          track("login_completed", { method: "email", source: authSource || "direct" });
          window.location.href = held;
          return;
        }
        const intent = consumePendingAuthIntent(returnTo || defaultPostAuthPath(signedIn));
        track("login_completed", {
          method: "email",
          source: intent.source || authSource || "direct",
        });
        window.location.href = intent.returnTo;
      } else {
        await signUp(email, password, marketingConsent);
        track("signup", { method: "email", source: authSource || "direct" });
        track("register_completed", { method: "email", source: authSource || "direct" });
        if (typeof window.gtag === "function") {
          window.gtag("event", "conversion", {
            send_to: "AW-17789498497/UZljCLvYyPYbEIHp2KJC",
            value: 1.0,
            currency: "USD",
          });
        }
        const verifyParams = new URLSearchParams();
        if (returnTo) verifyParams.set("returnTo", returnTo);
        if (authSource) verifyParams.set("source", authSource);
        window.location.href = `/verify${verifyParams.size ? `?${verifyParams.toString()}` : ""}`;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Authentication failed";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToEmail = () => {
    setStep("email");
    setPassword("");
    setError("");
    setExists(null);
  };

  return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <img src="/app_icon.png" alt="DealHawk" className="h-12 w-12 rounded-[13px] shadow-[0_8px_30px_rgba(124,58,237,0.45)]" />
          {step === "email" ? (
            <div className="flex flex-col gap-2">
              <Title id="auth-form-title" className="text-[28px] font-medium leading-[1.15] tracking-[-0.03em]">{t("auth.headline")}</Title>
              <p className="text-[15px] text-[#A1A1AA]">{t("auth.unifiedSubtitle")}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <Title id="auth-form-title" className="text-[28px] font-medium leading-[1.15] tracking-[-0.03em]">
                {exists ? t("auth.enterPassword") : t("auth.createPassword")}
              </Title>
              <p className="text-[15px] text-[#A1A1AA]">
                {exists
                  ? t("auth.enterPasswordSubtitle", { email })
                  : t("auth.createPasswordSubtitle", { email })}
              </p>
              {!exists && <p className="text-[13px] font-medium text-[#A78BFA]">{t("auth.newMemberOffer")}</p>}
            </div>
          )}
        </div>

        {error && (
          <div className="flex animate-fade-in items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-[13px] text-red-300">
            <svg className="mt-0.5 h-4 w-4 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </div>
        )}

        {/* === STEP: EMAIL === */}
        {step === "email" && (
          <>
            <div className="grid grid-cols-2 gap-2.5">
              <GoogleButton
                onClick={() => {
                  track("auth_oauth_clicked", { method: "google" });
                  window.location.href = `${import.meta.env.VITE_API_URL}/auth/google`;
                }}
                className={OAUTH_BTN_CLASS}
              >
                Google
              </GoogleButton>
              <DiscordButton
                onClick={() => {
                  track("auth_oauth_clicked", { method: "discord" });
                  window.location.href = `${import.meta.env.VITE_API_URL}/auth/discord`;
                }}
                className={OAUTH_BTN_CLASS}
              >
                Discord
              </DiscordButton>
            </div>

            <div className="flex items-center gap-3.5 text-xs text-[#71717A]">
              <div className="h-px flex-grow bg-[#27272A]" />
              {t("auth.or")}
              <div className="h-px flex-grow bg-[#27272A]" />
            </div>

            <form className="flex flex-col gap-3" onSubmit={handleEmailSubmit}>
              <label htmlFor="email" className="sr-only">
                {t("auth.email")}
              </label>
              <AuthInput
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailAddressPlaceholder")}
                className={AUTH_INPUT_CLASS}
                required
              />
              <AuthButton type="submit" disabled={isLoading} className={PRIMARY_CLASS}>
                {isLoading ? t("auth.checking") : t("auth.continueWithEmail")}
              </AuthButton>
            </form>
          </>
        )}

        {/* === STEP: PASSWORD === */}
        {step === "password" && (
          <form className="flex flex-col gap-3" onSubmit={handlePasswordSubmit}>
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="text-sm font-medium text-[#D4D4D8]">
                {t("auth.password")}
              </label>
              {exists && (
                <a href="/forgot-password" className="text-[13px] font-medium text-[#A78BFA] transition-colors hover:text-[#C4B5FD]">
                  {t("auth.forgotPassword")}
                </a>
              )}
            </div>
            <div className="relative">
              <AuthInput
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={exists ? "••••••••" : t("auth.minCharacters")}
                className={`${AUTH_INPUT_CLASS} !pr-11`}
                required
                minLength={exists ? undefined : 8}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-[#71717A] transition-colors hover:text-white focus:outline-none"
                tabIndex={-1}
                aria-label="Toggle password visibility"
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                )}
              </button>
            </div>

            {!exists && (
              <label className="flex cursor-pointer items-start gap-2.5 text-[13px] leading-relaxed text-[#A1A1AA]">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="mt-[3px] h-4 w-4 flex-none cursor-pointer rounded accent-[#7C3AED]"
                />
                <span>{t("auth.marketingEmailsConsent")}</span>
              </label>
            )}

            {!exists && (
              <p className="text-[13px] leading-relaxed text-[#A1A1AA]">
                {(() => {
                  const raw = t("auth.iAgreeToCreate", { terms: "__TERMS__", privacy: "__PRIVACY__" });
                  return raw.split(/(__TERMS__|__PRIVACY__)/g).map((part, i) => {
                    if (part === "__TERMS__")
                      return (
                        <a key={i} href="/terms" className="text-[#D4D4D8] transition-colors hover:text-white">
                          {t("auth.termsOfService")}
                        </a>
                      );
                    if (part === "__PRIVACY__")
                      return (
                        <a key={i} href="/privacy" className="text-[#D4D4D8] transition-colors hover:text-white">
                          {t("auth.privacyPolicy")}
                        </a>
                      );
                    return <span key={i}>{part}</span>;
                  });
                })()}
              </p>
            )}

            <AuthButton type="submit" disabled={isLoading} className={PRIMARY_CLASS}>
              {isLoading
                ? exists
                  ? "Signing in..."
                  : t("auth.creatingAccount")
                : exists
                  ? t("auth.signinButton")
                  : t("auth.registerButton")}
            </AuthButton>
            <button
              type="button"
              onClick={goBackToEmail}
              className="inline-flex h-11 items-center justify-center gap-1.5 text-sm text-[#A1A1AA] transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("auth.differentEmail")}
            </button>
          </form>
        )}
      </div>
  );
}

/** "By signing up you agree to the Terms of Service and Privacy Policy", under the email step. */
export function AuthTermsNote() {
  const { t } = useTranslation();
  return (
    <p className="text-[13px] leading-relaxed text-[#71717A]">
      {t("auth.bySigningUpYouAgree")}{" "}
      <a href="/terms" className="text-[#D4D4D8] transition-colors hover:text-white">
        {t("auth.termsOfService")}
      </a>{" "}
      {t("auth.and")}{" "}
      <a href="/privacy" className="text-[#D4D4D8] transition-colors hover:text-white">
        {t("auth.privacyPolicy")}
      </a>
    </p>
  );
}
